<script setup lang="ts">
import LoadingComponent from '@/components/LoadingComponent.vue';
import Card from 'primevue/card';
import { ref, watch, computed } from 'vue';
import CustomButton from '@/components/CustomComponent/CustomButton.vue';
import type { ToastMessageOptions } from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import type { ReportRequestByDate } from '@/api/models/reports/ReportRequestByDate';
import ApiService from '@/api/ApiService';
import type { PigmyReport } from '@/api/models/reports/PigmyReport';
import ConversionUtility from '@/utils/ConversionUtility';
import Storage from '@/utils/Storage';
import router from '@/router';
import * as XLSX from 'xlsx'

const loadingProgress = ref<number>(50)
const toast = useToast()
const minDate = ref(new Date(2024, 8, 1))
const maxDate = ref(new Date())
const fromDate = ref<Date>()
const toDate = ref<Date | null>()
const isLoading = ref<boolean>(false)
const FDate = ref()
const EDate = ref()
const reportDetails = ref<PigmyReport>()
const agentColumns = [
  { field:"agentId", header:"Agent ID", icon: 'pi pi-id-card' },
  { field:"agentName", header:"Agent Name", icon: 'pi pi-user' },   
  { field:"totalCollection", header:"Total Money Collected", icon: 'pi pi-money-bill' },
]

function viewReports(){
    if(!fromDate.value){
        showToast("From Date cannot be empty", "Please Select From Date", 'error')
        return
    }

    if(!toDate.value){
        showToast("To Date cannot be empty", "Please Select To Date", 'error')
        return
    }
    isLoading.value = true

    FDate.value = new Date(fromDate.value).setHours(0, 0, 0, 0);
    EDate.value = new Date(toDate.value).setHours(23, 59, 59, 999);
    
    const pigmyRequest : ReportRequestByDate ={
      startDate: FDate.value,
      endDate: EDate.value
    }

    ApiService.pigmyReport(pigmyRequest)
    .then(
      (response) => {
        setTimeout(() => {
        if (response.statusCode == 401) {
          Storage.clearData()
          router.push({
            name: 'login'
          })
        }
      }, 2000)
        if(response.statusCode == 200){
          reportDetails.value = response.data
        }
      }
    )
    .catch((error) => {
      showToast("Settings",error, "error")
    })
    .finally(() => {
      isLoading.value = false
    })
}

function handleDataTableProps(index: number, data: any): string {
  switch (index) {
    case 0: {
      return data.agentId
    }
    case 1: {
      return data.agentName
    }
    case 2: {
      return ConversionUtility.toStringRupeesConversion(data.totalCollection)
    }
    default: {
      return 'NA'
    }
  }
}

function showToast(summary: string, detail: string, severity: ToastMessageOptions['severity']) {
  toast.add({
    severity: severity,
    summary: summary,
    detail: detail,
    life: 5000
  })
}

function download() {
  const reportData = [
    [{ v: "Bhavasar Patini Sahakar Sanga", s: { font: { bold: true, sz: 18 }, alignment: { horizontal: "center" } } }, "", ""],
    [{ v: "Tq: Badami, Dis: Bagalkot", s: { font: { bold: true, sz: 16 }, alignment: { horizontal: "center" } } }, "", ""],
    [],
    ["Pigmy Report"],
    ["From:", ConversionUtility.toDateFormat(reportDetails.value?.startDate!!)],
    ["To:", ConversionUtility.toDateFormat(reportDetails.value?.endDate!!)],
    ["Total Pigmy Deposited:", ConversionUtility.toRuppesCoversion(reportDetails.value?.totalPigmyDeposited!!)],
    ["Total Pigmy Withdrawal:", ConversionUtility.toRuppesCoversion(reportDetails.value?.totalPigmyWithdrawal!!)],
    ["Total Pigmy Transactions:", reportDetails.value?.totalPigmyTransaction],
    ["Total Penalty Amount:", ConversionUtility.toRuppesCoversion(reportDetails.value?.totalPenaltyAmount!!)],
    ["Total Pigmy Accounts:", reportDetails.value?.totalPigmyAccounts],
    ["New Pigmy Accounts:", reportDetails.value?.newPigmyAccounts],
    ["Closed Pigmy Accounts:", reportDetails.value?.closedPigmyAccounts],
    [],
    ["Agent Wise Collection Details"],
    ["Agent ID", "Agent Name", "Total Collection"]
  ];

  reportDetails.value?.agentWiseDetails.forEach(agent => {
    reportData.push([
      agent.agentId,
      agent.agentName,
      ConversionUtility.toRuppesCoversion(agent.totalCollection)
    ]);
  });

  const worksheet = XLSX.utils.aoa_to_sheet(reportData);

  // Merge first cell with 3 columns to center the bank name
  worksheet["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 2 } } // Merge A1:C1
  ];

  worksheet["!cols"] = [
    { wch: 25 },
    { wch: 25 },
    { wch: 15 }
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

  loadingProgress.value = 50;

  // Generate Excel file as an array buffer
  const workbookArrayBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

  // Convert the array buffer to a Blob
  const blob = new Blob([workbookArrayBuffer], { type: "application/octet-stream" });
  loadingProgress.value = 75;

  // Create a download link and trigger the download
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute(
    "download",
    `Pigmy_Report_${ConversionUtility.toDateFormat(reportDetails.value?.startDate!!)}_to_${ConversionUtility.toDateFormat(reportDetails.value?.endDate!!)}.xlsx`
  );
  document.body.appendChild(link);
  link.click();
}


watch(fromDate, (newFromDate) => {
  if (newFromDate && toDate.value) {
    toDate.value = null;
  }
});
</script>

<template>
    <Toast class="error-p-toast" />
    <main class="main">
      <Card class="search-card dashboard-card">
        <template #title>
          <div class="card-header">
            <i class="pi pi-chart-bar report-icon"></i>
            <h2 class="title-right text-3xl font-bold">Pigmy Reports</h2>
          </div>
        </template>
        <template #content>

          <div class="date-filter-container">
            <div class="date-inputs">
              <FloatLabel class="date-field">
                <label for="fromDate" class="mandatory">From</label>
                <DatePicker v-model="fromDate" id="fromDate" class="input-text" :min-date="minDate" :max-date="maxDate" dateFormat="dd/mm/yy" showIcon iconClass="pi pi-calendar"/>
              </FloatLabel>

              <FloatLabel class="date-field">
                <label for="toDate" class="mandatory">To</label>
                <DatePicker v-model="toDate" id="toDate" class="input-text" :min-date="fromDate" :maxDate="maxDate" dateFormat="dd/mm/yy" showIcon iconClass="pi pi-calendar"/>
              </FloatLabel>
            </div>
            
            <CustomButton title="View Reports" @click="viewReports" class="view-btn" icon="pi pi-search"/>
          </div>
          
          <div v-if="!reportDetails && !isLoading" class="empty-state">
            <i class="pi pi-file-excel empty-icon"></i>
            <p>Select a date range and click "View Reports" to generate Pigmy reports</p>
          </div>
          
          <Fieldset v-if="reportDetails" class="report-fieldset" legend="Pigmy Report Details">
            <div class="date-range-banner">
              <i class="pi pi-calendar"></i>
              <span>{{ ConversionUtility.toDateFormat(reportDetails?.startDate) }} to {{ ConversionUtility.toDateFormat(reportDetails?.endDate)}}</span>
            </div>
            
            <div class="report-summary-container">
              <div class="report-card">
                <div class="report-card-header">
                  <i class="pi pi-wallet icon-primary"></i>
                  <h3>Deposits & Withdrawals</h3>
                </div>
                <div class="report-card-body">
                  <div class="report-item">
                    <i class="pi pi-arrow-down icon-success"></i>
                    <span class="label">Total Deposited:</span>
                    <span class="value">{{ConversionUtility.toStringRupeesConversion(reportDetails?.totalPigmyDeposited)}}</span>
                  </div>
                  <div class="report-item">
                    <i class="pi pi-arrow-up icon-danger"></i>
                    <span class="label">Total Withdrawal:</span>
                    <span class="value">{{ConversionUtility.toStringRupeesConversion(reportDetails?.totalPigmyWithdrawal)}}</span>
                  </div>
                  <div class="report-item">
                    <i class="pi pi-exclamation-triangle icon-warning"></i>
                    <span class="label">Total Penalty:</span>
                    <span class="value">{{ConversionUtility.toStringRupeesConversion(reportDetails?.totalPenaltyAmount)}}</span>
                  </div>
                </div>
              </div>
              
              <div class="report-card">
                <div class="report-card-header">
                  <i class="pi pi-users icon-primary"></i>
                  <h3>Accounts</h3>
                </div>
                <div class="report-card-body">
                  <div class="report-item">
                    <i class="pi pi-book icon-info"></i>
                    <span class="label">Total Accounts:</span>
                    <span class="value">{{reportDetails?.totalPigmyAccounts}}</span>
                  </div>
                  <div class="report-item">
                    <i class="pi pi-plus-circle icon-success"></i>
                    <span class="label">New Accounts:</span>
                    <span class="value">{{reportDetails?.newPigmyAccounts}}</span>
                  </div>
                  <div class="report-item">
                    <i class="pi pi-times-circle icon-danger"></i>
                    <span class="label">Closed Accounts:</span>
                    <span class="value">{{reportDetails?.closedPigmyAccounts}}</span>
                  </div>
                </div>
              </div>
              
              <div class="report-card">
                <div class="report-card-header">
                  <i class="pi pi-sync icon-primary"></i>
                  <h3>Transactions</h3>
                </div>
                <div class="report-card-body transaction-summary">
                  <div class="transaction-count">
                    <i class="pi pi-list icon-info"></i>
                    <span class="label">Total Transactions:</span>
                    <span class="value highlight">{{reportDetails?.totalPigmyTransaction}}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <Divider align="center" v-if="reportDetails?.agentWiseDetails?.length">
              <span class="divider-text"><i class="pi pi-users"></i> Agent Collection Details</span>
            </Divider>
            
            <div v-if="reportDetails?.agentWiseDetails?.length" class="agent-table-container">
              <DataTable :value="reportDetails?.agentWiseDetails" 
                paginator :rows="10" 
                :rowsPerPageOptions="[10, 20, 30, 40]" 
                tableStyle="min-width: 50rem"
                stripedRows
                responsiveLayout="scroll"
                class="agent-table">
                <Column v-for="(col, index) in agentColumns"
                  :key="col.field"
                  :field="col.field"
                  :header="col.header"
                  headerStyle="background-color: var(--surface-card); font-weight: 600;">
                  <template #header>
                    <div class="column-header">
                      <i :class="col.icon"></i>
                      <span>{{ col.header }}</span>
                    </div>
                  </template>
                  <template #body="slotProps">
                    {{ handleDataTableProps(index, slotProps.data) }}
                  </template>
                </Column>
              </DataTable>
            </div>
            
            <div class="action-buttons">
              <CustomButton title="Download Report" @click="download" icon="pi pi-download" class="download-btn"/>
            </div>
          </Fieldset>
          
          <div v-if="!reportDetails && !isLoading && fromDate && toDate" class="no-data-container">
            <i class="pi pi-info-circle no-data-icon"></i>
            <Tag severity="info" class="no-data-tag">No Pigmy Reports found for the selected time period.</Tag>
          </div>

          <div v-if="isLoading">
            <LoadingComponent
              text="Please wait while we process the request!"
              :dialogVisiblity="isLoading"
            />
          </div>
        </template>
      </Card>
    </main>
</template>
<style>
/* General Styles */
.main {
  width: 100%;
  padding: 15px;
  box-sizing: border-box;
}

.dashboard-card {
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  width: 100%;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.report-icon {
  font-size: 1.75rem;
  color: var(--primary-color);
}

.mandatory::after {
  content: ' *';
  color: var(--red-500);
}

.divider-text {
  font-size: 1rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Date Filter Section */
.date-filter-container {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1.5rem;
  margin: 1.5rem 0;
  background-color: var(--surface-section);
  padding: 1.5rem;
  border-radius: 8px;
  width: 100%;
}

.date-inputs {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  flex: 1;
}

.date-field {
  flex: 1;
  min-width: 200px;
}

.input-text {
  height: 45px;
  width: 100%;
}

.view-btn {
  min-width: 150px;
  height: 45px;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  background-color: var(--surface-ground);
  border-radius: 8px;
  margin: 1rem 0;
  width: 100%;
}

.empty-icon {
  font-size: 3rem;
  color: var(--text-color-secondary);
  margin-bottom: 1rem;
}

/* Report Fieldset */
.report-fieldset {
  margin-top: 1.5rem;
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
}

.date-range-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: var(--surface-hover);
  padding: 0.75rem 1rem;
  border-radius: 6px;
  margin-bottom: 1.5rem;
  font-weight: 500;
  width: 100%;
}

/* Report Cards */
.report-summary-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  width: 100%;
}

.report-card {
  background-color: var(--surface-card);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
}

.report-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.report-card-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background-color: var(--surface-section);
  border-bottom: 1px solid var(--surface-border);
}

.report-card-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.report-card-body {
  padding: 1rem;
}

.report-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px dashed var(--surface-border);
}

.report-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.report-item .label {
  flex: 1;
  font-weight: 500;
}

.report-item .value {
  font-weight: 600;
}

.transaction-summary {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100px;
}

.transaction-count {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
}

.transaction-count .highlight {
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary-color);
}

/* Agent Table */
.agent-table-container {
  margin: 1rem 0 2rem;
  width: 100%;
}

.agent-table {
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
}

.column-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
  width: 100%;
}

.download-btn {
  min-width: 180px;
}

/* No Data State */
.no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  margin: 1rem 0;
  background-color: var(--surface-ground);
  border-radius: 8px;
  width: 100%;
}

.no-data-icon {
  font-size: 2.5rem;
  color: var(--blue-500);
  margin-bottom: 1rem;
}

.no-data-tag {
  font-size: 1rem;
}

/* Icon Colors */
.icon-primary {
  color: var(--primary-color);
}

.icon-success {
  color: var(--green-500);
}

.icon-danger {
  color: var(--red-500);
}

.icon-warning {
  color: var(--orange-500);
}

.icon-info {
  color: var(--blue-500);
}

/* Responsive Adjustments */
@media screen and (max-width: 768px) {
  .report-summary-container {
    grid-template-columns: 1fr;
  }
  
  .date-filter-container {
    flex-direction: column;
    align-items: stretch;
  }
  
  .view-btn {
    width: 100%;
  }
}
</style>
